import { useCallback, useEffect, useState } from "react";
import {
  isRouteErrorResponse,
  useNavigate,
  useRouteError,
} from "react-router";
import { useTranslation } from "react-i18next";
import { Button, Result, Spin } from "antd";
import ClearCacheGuide from "components/ClearCacheGuide";
import { isChunkLoadError } from "utils/sentry";
import {
  recoverFromChunkError,
  resetChunkRecoveryAttempts,
} from "utils/chunkRecovery";

const ErrorPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const clickHandler = useCallback(() => navigate("/"), [navigate]);
  const error = useRouteError();
  const isChunkError = isChunkLoadError(error);
  const [recovering, setRecovering] = useState(isChunkError);

  const recover = useCallback(
    (source) => {
      setRecovering(true);
      return recoverFromChunkError(error, source).then((reloading) => {
        if (!reloading) setRecovering(false);
      });
    },
    [error],
  );

  useEffect(() => {
    if (isChunkError) recover("route_error");
  }, [isChunkError, recover]);

  const retryHandler = useCallback(() => {
    resetChunkRecoveryAttempts();
    recover("manual_retry");
  }, [recover]);

  if (isChunkError) {
    // Kartu putih supaya tetap terbaca di layout berlatar gelap (mis. login)
    return (
      <div className="mx-auto my-10 max-w-2xl rounded-lg bg-white shadow-md">
        {recovering ? (
          <Result
            icon={<Spin size="large" />}
            title={t("error_page.updating_app")}
            subTitle={t("error_page.updating_app_desc")}
          />
        ) : (
          <Result
            status="warning"
            title={t("error_page.update_failed")}
            subTitle={t("error_page.update_failed_desc")}
            extra={
              <Button type="primary" onClick={retryHandler}>
                {t("error_page.reload")}
              </Button>
            }
          >
            <ClearCacheGuide buttonLabel={t("error_page.reload")} />
          </Result>
        )}
      </div>
    );
  }

  let errorMessage = "";
  if (isRouteErrorResponse(error)) {
    errorMessage = error.statusText;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  }
  return (
    <Result
      status="500"
      title="500"
      className="text-white"
      subTitle={`Sorry, something went wrong. ${errorMessage}`}
      extra={
        <Button type="primary" onClick={clickHandler}>
          Back Home
        </Button>
      }
    />
  );
};

export default ErrorPage;
